import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CommunityEventCard from './CommunityEventCard';
import PastEventCard from './PastEventCard';
import EventGalleryModal from './EventGalleryModal';
import { communityEventsAPI, eventsAPI, picturesAPI } from './services/api';
import { parseUTCDate } from './utils/dateUtils';
import './MapView.css';

const STATUS_COLORS = {
  pending: '#ff9800',
  approved: '#4caf50',
  past: '#9e9e9e',
  dangerous: '#f44336',
};

// 5-pointed star SVG path (normalized, centered at origin, pointing up)
const STAR_PATH =
  'M 0,-1 L 0.235,-0.324 L 0.951,-0.309 L 0.380,0.124 L 0.588,0.809 ' +
  'L 0,0.4 L -0.588,0.809 L -0.380,0.124 L -0.951,-0.309 L -0.235,-0.324 Z';

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getMarkerColor(event) {
  if (event._isPast) return STATUS_COLORS.past;
  if (event.status === -1) return STATUS_COLORS.dangerous;
  if (event.status === 0) return STATUS_COLORS.pending;
  if (event.status === 1) return STATUS_COLORS.approved;
  return STATUS_COLORS.approved;
}

function MapView({
  currentUser,
  followedEvents,
  filterShowPending,
  filterShowApproved,
  filterShowPast,
  onFollowToggle,
  onVote,
  onJoinEvent,
  onCreateGathering,
  onMarkAsDangerous,
  onCommentVote,
  onOpenGallery,
  onOrganizerClick,
}) {
  const { t, i18n } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mapEvents, setMapEvents] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);

  const [showGallery, setShowGallery] = useState(false);
  const [galleryPictures, setGalleryPictures] = useState([]);
  const [galleryEvent, setGalleryEvent] = useState(null);

  // Ref so the idle listener always calls the latest version of fetchViewportEvents
  const fetchViewportEventsRef = useRef(null);

  const fetchViewportEvents = useCallback(async () => {
    if (!mapInstanceRef.current) return;
    const bounds = mapInstanceRef.current.getBounds();
    const center = mapInstanceRef.current.getCenter();
    if (!bounds || !center) return;

    const ne = bounds.getNorthEast();
    const radiusKm = Math.ceil(haversineKm(center.lat(), center.lng(), ne.lat(), ne.lng()));

    setMapLoading(true);
    try {
      const fetches = [];
      if (filterShowPending)
        fetches.push(communityEventsAPI.getNearby(center.lat(), center.lng(), radiusKm, [0, -1], null, false, false, 1, 50)
          .then(r => {
            const now = new Date();
            return (r.items ?? r).filter(e => !e.dateTime || parseUTCDate(e.dateTime) > now);
          }));
      if (filterShowApproved)
        fetches.push(communityEventsAPI.getNearby(center.lat(), center.lng(), radiusKm, [1], null, false, false, 1, 50));
      if (filterShowPast)
        fetches.push(communityEventsAPI.getNearby(center.lat(), center.lng(), radiusKm, null, null, false, true, 1, 200).then(r => [
          ...(r.items ?? []).map(e => ({ ...e, _isPast: true })),
          ...(r.privateEvents ?? []).map(e => ({ ...e, _isPast: true })),
        ]));
      const results = await Promise.all(fetches);
      setMapEvents(results.flatMap(r => Array.isArray(r) ? r : (r.items ?? r)));
    } catch (err) {
      console.error('Error fetching map events:', err);
    } finally {
      setMapLoading(false);
    }
  }, [filterShowPending, filterShowApproved, filterShowPast]);

  fetchViewportEventsRef.current = fetchViewportEvents;

  // Initialize map once on mount
  useEffect(() => {
    const initMap = () => {
      if (!window.google?.maps) { setTimeout(initMap, 100); return; }
      if (mapInstanceRef.current) return;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: currentUser?.homeLatitude || 42.6977, lng: currentUser?.homeLongitude || 23.3219 },
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      // idle fires once after the map settles (pan or zoom) — Google debounces this for us
      mapInstanceRef.current.addListener('idle', () => fetchViewportEventsRef.current?.());
    };
    initMap();
  }, [currentUser]);

  // Re-fetch immediately when filter toggles change (without waiting for next map move)
  useEffect(() => {
    fetchViewportEvents();
  }, [fetchViewportEvents]);

  // Keep selectedEvent in sync when events refresh (e.g. after voting)
  useEffect(() => {
    if (!selectedEvent) return;
    const updated = mapEvents.find(e => e.id === selectedEvent.id);
    if (updated) setSelectedEvent(updated);
  }, [mapEvents]);

  // Place/update markers whenever events or followedEvents change
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    mapEvents.forEach(event => {
      if (!event.latitude || !event.longitude) return;
      const color = getMarkerColor(event);
      const isFollowed = followedEvents?.has(event.id);

      const marker = new window.google.maps.Marker({
        map: mapInstanceRef.current,
        position: { lat: event.latitude, lng: event.longitude },
        title: event.name,
        icon: isFollowed
          ? {
              path: STAR_PATH,
              fillColor: color,
              fillOpacity: 1,
              strokeColor: '#FFD700',
              strokeWeight: 2,
              scale: 14,
            }
          : {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: color,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 10,
            },
      });

      marker.addListener('click', () => setSelectedEvent(event));
      markersRef.current.push(marker);
    });
  }, [mapEvents, followedEvents]);


  const handleOpenEventGallery = async (event) => {
    setGalleryEvent(event);
    try {
      let pictures;
      if (event.communityEventId) {
        const [communityPics, eventPics] = await Promise.all([
          picturesAPI.getCommunityEventPictures(event.communityEventId),
          picturesAPI.getEventPictures(event.id),
        ]);
        const seen = new Set();
        pictures = [...communityPics, ...eventPics].filter(p => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
      } else {
        pictures = await picturesAPI.getEventPictures(event.id);
      }
      setGalleryPictures(pictures);
      setShowGallery(true);
    } catch (err) {
      console.error('Error loading event pictures:', err);
      alert(t('gatheringsSidebar.alerts.failedGallery'));
    }
  };


  return (
    <div className="map-view-wrapper">
      <div ref={mapRef} className="map-container" />
      {mapLoading && <div className="map-loading-indicator">Loading...</div>}

      {selectedEvent && (
        <div className="map-event-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="map-event-dialog" onClick={e => e.stopPropagation()}>
            <button className="map-event-close" onClick={() => setSelectedEvent(null)}>✕</button>
            {selectedEvent._isPast ? (
              <PastEventCard
                event={selectedEvent}
                currentUser={currentUser}
                onOpenGallery={handleOpenEventGallery}
                onOrganizerClick={onOrganizerClick}
              />
            ) : (
              <CommunityEventCard
                event={selectedEvent}
                currentUser={currentUser}
                showVoting={true}
                followedEvents={followedEvents}
                onFollowToggle={onFollowToggle}
                onVote={async (eventId, voteValue) => { await onVote(eventId, voteValue); fetchViewportEvents(); }}
                onJoinEvent={onJoinEvent}
                onCreateGathering={onCreateGathering}
                onMarkAsDangerous={onMarkAsDangerous}
                onCommentVote={onCommentVote}
                onOpenGallery={onOpenGallery}
                onOrganizerClick={onOrganizerClick}
              />
            )}
          </div>
        </div>
      )}

      {/* Event gallery */}
      {showGallery && galleryEvent && (
        <EventGalleryModal
          pictures={galleryPictures}
          event={galleryEvent}
          onClose={() => { setShowGallery(false); setGalleryEvent(null); }}
        />
      )}

    </div>
  );
}

export default MapView;
